<script setup>
import { Form } from 'vee-validate'
import { reactive, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import ImoContextMenu from '@/components/context-menu/ImoContextMenu.vue'
import CheckboxInput from '@/components/input/checkboxInput.vue'
import ClientInput from '@/components/input/clientInput.vue'
import ContactInput from '@/components/input/contactInput.vue'
import DateInput from '@/components/input/dateInput.vue'
import MultiBoxInput from '@/components/input/multiBoxInput.vue'
import MultiRadioInput from '@/components/input/multiRadioInput.vue'
import SelectInput from '@/components/input/selectInput.vue'
import TextInput from '@/components/input/textInput.vue'
import ValueCentsInput from '@/components/input/valueCentsInput.vue'
import WalletInput from '@/components/input/walletInput.vue'
import { useFormatTools } from '@/composables/useFormatTools'

const componentMap = {
  CheckboxInput,
  ClientInput,
  ContactInput,
  ImoContextMenu,
  MultiBoxInput,
  MultiRadioInput,
  SelectInput,
  TextInput,
  WalletInput,
  DateInput,
  ValueCentsInput,
}

const emit = defineEmits(['filter', 'selectWallet', 'payerName'])

const props = defineProps({
  filter_form_name: {
    type: String,
    default: 'filterform',
  },
  fields: {
    type: Array,
    required: true,
  },
  saveQuery: {
    type: Boolean,
    default: true,
  },
  defaultFilterQuery: {
    type: Function,
    default: () => ({
      page: 1,
      custom_page_size: '30',
      ordering: '-created_at',
    }),
  },
  // Escopo que o usuário não pode alterar (ex.: cliente vinculado do admin — task 86akqpnf2).
  // Sobrescreve a query restaurada da URL, o padrão, o "limpar" e qualquer edição.
  lockedQuery: {
    type: Object,
    default: () => ({}),
  },
  alignLeft: {
    type: Boolean,
    default: false,
  },
  inLine: {
    type: Boolean,
    default: false,
  },
  fetchOnCreate: {
    type: Boolean,
    default: false,
  },
  hideButtons: {
    type: Boolean,
    default: false,
  },
  order: {
    type: Boolean,
    default: false,
  },
  client: {
    type: String,
    required: false,
  },
  rawQuery: {
    type: Boolean,
    default: false,
  },
})

const { stringifyQuery } = useFormatTools()
const route = useRoute()
const router = useRouter()
const query = reactive({})
const ordering = [
  { value: '-created_at', label: 'Do mais recente' },
  { value: '+created_at', label: 'Do menos recente' },
]

const page = [
  { value: '30', label: '30' },
  { value: '50', label: '50' },
  { value: '100', label: '100' },
  { value: '-1', label: 'Todos' },
]

onMounted(() => {
  if (Object.keys(route.query).length !== 0 && props.saveQuery) {
    // query.value = { ...route.query }
    Object.assign(query, JSON.parse(JSON.stringify(route.query)))
  } else {
    // query.value = { ...props.defaultFilterQuery() }
    Object.assign(query, props.defaultFilterQuery())
  }
  Object.assign(query, props.lockedQuery)
  if (props.fetchOnCreate) {
    if (props.rawQuery) {
      emit('filter', query)
      return
    }
    emit('filter', formatQuery())
  }
})

const handleClick = () => {
  query.page = 1
  submit()
}

const submit = () => {
  Object.assign(query, props.lockedQuery)
  if (props.saveQuery) {
    setQuery(query)
  }
  if (props.rawQuery) {
    emit('filter', query)
    return
  }
  emit('filter', formatQuery())
}

const cleanFilter = () => {
  Object.keys(query).forEach(key => delete query[key])

  // Preenche com os valores padrão
  Object.assign(query, props.defaultFilterQuery(), props.lockedQuery)
  submit()
}

// As chaves travadas ficam fora da URL: são reaplicadas a cada mount/submit/limpar.
// Gravá-las faria o escopo antigo (ex.: cliente desvinculado) ser restaurado no
// campo do filtro depois que o escopo some.
const setQuery = data => {
  const queryCopy = JSON.parse(JSON.stringify(data))
  Object.keys(props.lockedQuery).forEach(key => delete queryCopy[key])
  router.replace({ query: queryCopy }).catch(() => {})
}

const formatQuery = () => {
  // "search" é normalizado inteiramente no backend (PhoneService/DocumentUtils
  // via CustomSearchFilter.STRUCTURED_SEARCH_NORMALIZERS) — o front só precisa
  // mandar o termo bruto.
  if (query.search) {
    return stringifyQuery(query)
  }

  // "transfer_receiver_document" é um filtro exato (não passa pelo
  // CustomSearchFilter), então a limpeza de máscara continua necessária aqui.
  const cleanAndValidateDocument = document => {
    const cleanedDocument = document ? document.replace(/[^0-9]/g, '') : ''
    return cleanedDocument.length === 11 || cleanedDocument.length === 14
      ? cleanedDocument
      : document
  }

  const transferReceiverDocument = query.transfer_receiver_document || ''
  return stringifyQuery({
    ...query,
    transfer_receiver_document: cleanAndValidateDocument(transferReceiverDocument),
  })
}

watch(
  () => query.custom_page_size,
  (newVal, oldVal) => {
    if (newVal !== oldVal && oldVal !== undefined) {
      query.page = 1
      query.custom_page_size = newVal
      submit()
    }
  },
)

defineExpose({
  submit,
  query,
  cleanFilter,
})
</script>

<template>
  <div :class="{ 'flex flex-row flex-wrap items-end gap-4': inLine }">
    <Form class="flex flex-col gap-4" :id="filter_form_name" @submit="handleClick">
      <h1 class="text-primary-90 font-medium">Filtros</h1>
      <div
        v-for="(section, section_index) in fields"
        :key="section_index"
        class="flex flex-col gap-3.5 sm:flex-row"
      >
        <div v-for="(item, index) in section" :key="index" class="w-full flex-col sm:flex-row">
          <component
            :is="componentMap[item.component]"
            v-model="query[item.name]"
            :type="item.type"
            :label="item.label"
            :id="item.id"
            :options="item.options"
            :class="item.class"
            :placeholder="item.placeholder"
            :name="item.name"
            :onlyPrincipal="item.onlyPrincipal"
            :single="item.single"
            :client="client"
          />
        </div>
      </div>
      <div class="flex flex-col gap-1">
        <h1 v-if="order" class="text-primary-90 mt-2 font-medium">Ordenação da Tabela</h1>
        <div
          class="flex flex-col items-end gap-2 sm:flex-row"
          :class="{
            'justify-end': !order,
          }"
        >
          <SelectInput
            v-if="order"
            v-model="query['custom_page_size']"
            label="Quantidade"
            class="w-full max-w-[144px]"
            :options="page"
            name="custom_page_size"
          />
          <SelectInput
            v-if="order"
            v-model="query['ordering']"
            label="Organizar"
            class="w-full max-w-[220px]"
            name="ordering"
            :options="ordering"
          />
          <div class="flex gap-2">
            <ImoButton
              v-if="!hideButtons"
              variant="tertiary"
              @click="cleanFilter"
              type="button"
              text="Limpar"
              class="mb-1"
            />
            <ImoButton
              v-if="!hideButtons"
              variant="secondary"
              :form="filter_form_name"
              type="submit"
              icon="search-normal"
              text="Buscar"
              class="mb-1"
            />
          </div>
        </div>
      </div>
    </Form>
  </div>
</template>
